import { create } from "zustand";
import { devtools } from "zustand/middleware";
import axios from "axios";

function unwrapLeaveApplicationsList(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.data)) return res.data.data;
  return [];
}

function unwrapLeaveApplicationFromResponse(res) {
  if (!res || typeof res !== "object") return null;
  if (res.id) return res;
  if (res.data?.data?.id) return res.data.data;
  if (res.data?.id) return res.data;
  if (res.application?.id) return res.application;
  return null;
}

const isEmptyObject = (value) =>
  !value || (typeof value === "object" && Object.keys(value).length === 0);

const initialState = {
  leaveApplications: {},
  loading: false,
  error: {},
  myApplications: {},
  gettingMyApplications: false,
  myApplicationsError: {},
  selectedApplication: {},
  gettingSelectedApplication: false,
  selectedApplicationError: {},
  showLeaveApplicationDetails: false,
  addingLeaveApplication: false,
  showAddLeaveForm: false,
  applicationToEdit: {},
  creating: false,
  creatingError: {},
  updating: false,
  updatingError: {},
  deleting: false,
  deletingError: {},
  submittingToSupervisor: false,
  submittingToSupervisorError: {},
  checkingLeaveApplication: false,
  checkingLeaveApplicationError: {},
  rejectingLeaveApplication: false,
  rejectingLeaveApplicationError: {},
  approvingLeaveApplication: false,
  approvingLeaveApplicationError: {},
  undoApprovingLeaveApplication: false,
  undoApprovingLeaveApplicationError: {},
  cancellingLeaveApplication: false,
  cancellingLeaveApplicationError: {},
  approvalModalVisible: false,
  approvalModalAction: null,
};

const store = (set, get) => ({
  ...initialState,

  refreshLeaveLists: () => {
    get().getMyLeaveApplications();
    if (!isEmptyObject(get().leaveApplications)) get().getLeaveApplications();
  },

  getLeaveApplications: async () => {
    set({ loading: true, error: {} });

    await axios
      .get("/administration/leave-applications")
      .then((res) => {
        const rows = unwrapLeaveApplicationsList(res);
        set({
          leaveApplications: {
            ...(typeof res === "object" && res !== null ? res : {}),
            data: rows,
          },
        });
      })
      .catch((error) => {
        set({ error });
      })
      .finally(() => {
        set({ loading: false });
      });
  },

  getMyLeaveApplications: async () => {
    set({ gettingMyApplications: true, myApplicationsError: {} });

    await axios
      .get("/administration/leave-applications/my-applications")
      .then((res) => {
        const rows = unwrapLeaveApplicationsList(res);
        set({
          myApplications: {
            ...(typeof res === "object" && res !== null ? res : {}),
            data: rows,
          },
        });
      })
      .catch((error) => {
        set({ myApplicationsError: error });
      })
      .finally(() => {
        set({ gettingMyApplications: false });
      });
  },

  getLeaveApplicationDetails: async (id) => {
    if (!id) {
      set({
        selectedApplicationError: { message: "No application ID provided" },
        gettingSelectedApplication: false,
      });
      return;
    }

    set({
      gettingSelectedApplication: true,
      selectedApplicationError: {},
      showLeaveApplicationDetails: true,
      applicationToEdit: {},
      addingLeaveApplication: false,
    });

    await axios
      .get(`/administration/leave-applications/${id}`)
      .then((res) => {
        let applicationData = unwrapLeaveApplicationFromResponse(res);

        if (!applicationData?.id && res?.data) {
          if (res.data.data?.id) applicationData = res.data.data;
          else if (res.data.id) applicationData = res.data;
        }

        if (applicationData && applicationData.id) {
          set({
            selectedApplication: applicationData,
            selectedApplicationError: {},
          });
        } else {
          set({
            selectedApplicationError: {
              message: "Leave application data is empty or invalid",
            },
            selectedApplication: {},
          });
        }
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.server?.message ||
          error?.message ||
          "Unable to fetch leave application details";
        set({
          selectedApplicationError: { message: errorMessage, fullError: error },
          selectedApplication: {},
        });
      })
      .finally(() => {
        set({ gettingSelectedApplication: false });
      });
  },

  createLeaveApplication: async (data) => {
    set({ creating: true, creatingError: {} });

    await axios
      .post("/administration/leave-applications", data)
      .then(async () => {
        get().setAddingLeaveApplication(false, false);
        get().refreshLeaveLists();
      })
      .catch((error) => {
        set({ creatingError: error });
      })
      .finally(() => {
        set({ creating: false });
      });
  },

  updateLeaveApplication: async (id, data) => {
    set({ updating: true, updatingError: {} });

    await axios
      .put(`/administration/leave-applications/${id}`, data)
      .then(() => {
        get().setAddingLeaveApplication(false, false);
        get().refreshLeaveLists();
        get().getLeaveApplicationDetails(id);
      })
      .catch((error) => {
        set({ updatingError: error });
      })
      .finally(() => {
        set({ updating: false });
      });
  },

  deleteLeaveApplication: async (id) => {
    set({ deleting: true, deletingError: {} });

    await axios
      .delete(`/administration/leave-applications/${id}`)
      .then(() => {
        set({ showLeaveApplicationDetails: false, selectedApplication: {} });
        get().refreshLeaveLists();
      })
      .catch((error) => {
        set({ deletingError: error });
      })
      .finally(() => {
        set({ deleting: false });
      });
  },

  submitToSupervisor: async (id) => {
    set({ submittingToSupervisor: true, submittingToSupervisorError: {} });

    await axios
      .patch(`/administration/leave-applications/submit/${id}`)
      .then(() => {
        get().refreshLeaveLists();
        get().getLeaveApplicationDetails(id);
      })
      .catch((error) => {
        set({ submittingToSupervisorError: error });
      })
      .finally(() => {
        set({ submittingToSupervisor: false });
      });
  },

  checkLeaveApplication: async (id, body) => {
    set({ checkingLeaveApplication: true, checkingLeaveApplicationError: {} });

    await axios
      .patch(`/administration/leave-applications/check/${id}`, body ?? {})
      .then(() => {
        get().refreshLeaveLists();
        get().getLeaveApplicationDetails(id);
      })
      .catch((error) => {
        set({ checkingLeaveApplicationError: error });
      })
      .finally(() => {
        set({ checkingLeaveApplication: false });
      });
  },

  rejectLeaveApplication: async (id, body) => {
    set({ rejectingLeaveApplication: true, rejectingLeaveApplicationError: {} });

    await axios
      .patch(`/administration/leave-applications/reject/${id}`, body ?? {})
      .then(() => {
        get().refreshLeaveLists();
        get().getLeaveApplicationDetails(id);
      })
      .catch((error) => {
        set({ rejectingLeaveApplicationError: error });
      })
      .finally(() => {
        set({ rejectingLeaveApplication: false });
      });
  },

  approveLeaveApplication: async (id, body) => {
    set({ approvingLeaveApplication: true, approvingLeaveApplicationError: {} });

    await axios
      .patch(`/administration/leave-applications/approve/${id}`, body ?? {})
      .then(() => {
        get().refreshLeaveLists();
        get().getLeaveApplicationDetails(id);
      })
      .catch((error) => {
        set({ approvingLeaveApplicationError: error });
      })
      .finally(() => {
        set({ approvingLeaveApplication: false });
      });
  },

  undoApproveLeaveApplication: async (id) => {
    set({ undoApprovingLeaveApplication: true, undoApprovingLeaveApplicationError: {} });

    await axios
      .patch(`/administration/leave-applications/undo-approve/${id}`)
      .then(() => {
        get().refreshLeaveLists();
        get().getLeaveApplicationDetails(id);
      })
      .catch((error) => {
        set({ undoApprovingLeaveApplicationError: error });
      })
      .finally(() => {
        set({ undoApprovingLeaveApplication: false });
      });
  },

  cancelLeaveApplication: async (id) => {
    set({ cancellingLeaveApplication: true, cancellingLeaveApplicationError: {} });

    await axios
      .patch(`/administration/leave-applications/cancel/${id}`)
      .then(() => {
        get().refreshLeaveLists();
        get().getLeaveApplicationDetails(id);
      })
      .catch((error) => {
        set({ cancellingLeaveApplicationError: error });
      })
      .finally(() => {
        set({ cancellingLeaveApplication: false });
      });
  },

  showApprovalModal: (action) => {
    set({ approvalModalAction: action, approvalModalVisible: true });
  },

  hideApprovalModal: () => {
    set({ approvalModalVisible: false, approvalModalAction: null });
  },

  setAddingLeaveApplication: (adding = false, showForm = false, formData = {}) => {
    set({
      addingLeaveApplication: adding,
      showAddLeaveForm: showForm,
      applicationToEdit: formData,
    });

    if (adding === false) {
      set({ showLeaveApplicationDetails: false });
    } else {
      set({ selectedApplication: {} });
    }
  },

  setShowLeaveApplicationDetails: (show = false) => {
    set({ showLeaveApplicationDetails: show });
  },

  setSelectedApplication: (application = {}) => {
    set({ selectedApplication: application });
  },
});

const useLeaveStore = create(devtools(store, { name: "Leave Store" }));

export default useLeaveStore;
